import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/App";
import { toast, Toaster } from "sonner";
import {
  FileText,
  Download,
  Copy,
  Check,
  Loader2,
  ArrowLeft,
  Calendar,
  DollarSign,
  RefreshCw,
  Sparkles,
  FileOutput,
  Save,
  Share2,
  Link2,
  ExternalLink,
  PenTool,
  Mail,
  CheckCircle2,
  BookOpen,
  Scale,
  Briefcase,
  Code
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EntitlementGate from "@/components/EntitlementGate";

const SERVICE_TYPES = [
  { value: "design", label: "Design Services" },
  { value: "dev", label: "Development Services" },
  { value: "copywriting", label: "Copywriting Services" }
];

// Inline Template Quick Picker for Contract Builder
function TemplateQuickPicker({ onSelect }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get("/contracts/templates");
        setTemplates(res.data.templates || []);
      } catch (e) {
        console.error("Failed to load templates");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const CATEGORY_ICONS = {
    legal: Scale,
    service: Briefcase,
    creative: PenTool,
    technical: Code,
    professional: BookOpen,
    custom: FileText
  };

  const CATEGORY_COLORS = {
    legal: "bg-red-500/20 text-red-400",
    service: "bg-blue-500/20 text-indigo-400",
    creative: "bg-indigo-500/20 text-indigo-400",
    technical: "bg-green-500/20 text-green-400",
    professional: "bg-indigo-500/20 text-indigo-400",
    custom: "bg-slate-500/20 text-indigo-400"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-8">
        <FileText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
        <p className="text-indigo-400 text-sm">No templates yet</p>
        <Link to="/contract-templates" className="text-indigo-400 hover:text-indigo-400 text-sm mt-2 inline-block">
          Create your first template →
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {templates.slice(0, 6).map((template) => {
        const CategoryIcon = CATEGORY_ICONS[template.category] || FileText;
        return (
          <button
            key={template.id}
            onClick={() => onSelect(template)}
            className="p-3 bg-slate-900/50 border border-slate-700 hover:border-indigo-500/50 rounded-lg text-left transition-colors group"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${CATEGORY_COLORS[template.category] || CATEGORY_COLORS.custom}`}>
              <CategoryIcon className="w-4 h-4" />
            </div>
            <h4 className="text-sm font-medium text-indigo-500 group-hover:text-indigo-400 truncate">
              {template.name}
            </h4>
            <p className="text-xs text-slate-400 truncate">{template.category}</p>
          </button>
        );
      })}
      {templates.length > 6 && (
        <Link
          to="/contract-templates"
          className="p-3 bg-slate-900/50 border border-dashed border-slate-700 hover:border-indigo-500/50 rounded-lg flex flex-col items-center justify-center transition-colors"
        >
          <span className="text-indigo-400 text-sm">View All</span>
          <span className="text-slate-400 text-xs">{templates.length - 6} more</span>
        </Link>
      )}
    </div>
  );
}

export default function ContractBuilder({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [autoSaved, setAutoSaved] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [sendingForSignature, setSendingForSignature] = useState(false);
  
  // Contract state
  const [contract, setContract] = useState(null);
  const [mdPreview, setMdPreview] = useState("");
  
  // Form inputs
  const [serviceType, setServiceType] = useState("design");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("Net 7. Work may pause if invoices are overdue.");
  const [revisionPolicy, setRevisionPolicy] = useState("1 round included; extras billed.");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [freelancerName, setFreelancerName] = useState("");
  
  // Template selection
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Auto-save timer ref
  const autoSaveTimer = useRef(null);
  const draftKey = `contract_draft_${projectId}`;

  // Load saved draft from localStorage
  useEffect(() => {
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setServiceType(draft.serviceType || "design");
        setStartDate(draft.startDate || "");
        setEndDate(draft.endDate || "");
        setPaymentTerms(draft.paymentTerms || "Net 7. Work may pause if invoices are overdue.");
        setRevisionPolicy(draft.revisionPolicy || "1 round included; extras billed.");
        setAdditionalNotes(draft.additionalNotes || "");
        setFreelancerName(draft.freelancerName || "");
      } catch (e) {
        // Invalid saved draft
      }
    }
  }, [projectId]);

  // Auto-save draft to localStorage (debounced)
  const saveDraft = useCallback(() => {
    const draft = {
      serviceType,
      startDate,
      endDate,
      paymentTerms,
      revisionPolicy,
      additionalNotes,
      freelancerName,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(draftKey, JSON.stringify(draft));
    setAutoSaved(true);
    setTimeout(() => setAutoSaved(false), 2000);
  }, [serviceType, startDate, endDate, paymentTerms, revisionPolicy, additionalNotes, freelancerName, draftKey]);

  // Debounced auto-save on form changes
  useEffect(() => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = setTimeout(() => {
      saveDraft();
    }, 1500); // Save after 1.5 seconds of no changes
    
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [serviceType, startDate, endDate, paymentTerms, revisionPolicy, additionalNotes, freelancerName]);

  // Clear draft when contract is generated
  const clearDraft = () => {
    localStorage.removeItem(draftKey);
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    
    // Pre-fill the markdown preview with template content
    if (template.md_body) {
      // Replace placeholders with project/client data if available
      let content = template.md_body;
      if (client) {
        content = content.replace(/{client_name}/g, client.name || client.company || "");
        content = content.replace(/{client_email}/g, client.email || "");
        content = content.replace(/{client_company}/g, client.company || "");
      }
      if (project) {
        content = content.replace(/{project_title}/g, project.title || "");
        content = content.replace(/{project_budget}/g, project.budget ? `$${project.budget}` : "");
      }
      if (freelancerName || user?.name) {
        content = content.replace(/{freelancer_name}/g, freelancerName || user?.name || "");
      }
      content = content.replace(/{date}/g, new Date().toLocaleDateString());
      
      setMdPreview(content);
      setAdditionalNotes(`Using template: ${template.name}`);
    }
    
    // Set service type if template has one
    if (template.service_type) {
      setServiceType(template.service_type);
    }
    
    setShowTemplates(false);
    toast.success(`Template "${template.name}" loaded!`);
  };

  useEffect(() => {
    fetchData();
    
    // Check for pre-selected template from Contract Templates page
    const storedTemplate = sessionStorage.getItem("selectedContractTemplate");
    if (storedTemplate) {
      try {
        const template = JSON.parse(storedTemplate);
        handleTemplateSelect(template);
        sessionStorage.removeItem("selectedContractTemplate");
      } catch (e) {
        // Invalid stored template
      }
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      // Fetch project details
      const projRes = await api.get(`/projects/${projectId}`);
      setProject(projRes.data);
      
      // Fetch client details
      if (projRes.data.client_id) {
        const clientRes = await api.get(`/clients/${projRes.data.client_id}`);
        setClient(clientRes.data);
      }
      
      // Try to fetch existing contract
      try {
        const contractRes = await api.get(`/contracts/projects/${projectId}/contract`);
        setContract(contractRes.data);
        setMdPreview(contractRes.data.md_body || "");
        
        // Populate form from saved inputs
        const inputs = contractRes.data.inputs || {};
        setServiceType(contractRes.data.service_type || "design");
        setStartDate(inputs.start_date || "");
        setEndDate(inputs.end_date || "");
        setPaymentTerms(inputs.payment_terms || "Net 7. Work may pause if invoices are overdue.");
        setRevisionPolicy(inputs.revision_policy || "1 round included; extras billed.");
        setAdditionalNotes(inputs.additional_notes || "");
        setFreelancerName(inputs.freelancer_name || "");
        
        // Fetch signature status
        try {
          const sigRes = await api.get(`/contracts/projects/${projectId}/contract/signature`);
          setSignatureStatus(sigRes.data);
        } catch (e) {
          // No signature request yet
        }
      } catch (e) {
        // No existing contract - that's fine
      }
      
      // Set default freelancer name from user
      if (!freelancerName && user?.name) {
        setFreelancerName(user.name);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }
  };

  const generateContract = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/contracts/projects/${projectId}/contract`, {
        service_type: serviceType,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        payment_terms: paymentTerms || undefined,
        revision_policy: revisionPolicy || undefined,
        additional_notes: additionalNotes || undefined,
        freelancer_name: freelancerName || undefined
      });
      
      setMdPreview(res.data.md_body || "");
      setContract(res.data);
      clearDraft(); // Clear local draft after successful generation
      toast.success(`Contract generated! Version ${res.data.version}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "FEATURE_GATED") {
        toast.error("Contract Builder requires Pro or higher plan");
      } else {
        toast.error(detail?.message || detail || "Failed to generate contract");
      }
    } finally {
      setGenerating(false);
    }
  };

  const exportContract = async (format) => {
    if (!contract) {
      toast.error("Generate a contract first");
      return;
    }
    
    setExporting(true);
    try {
      if (format === "text") {
        // Download as text file
        const res = await api.post(
          `/contracts/projects/${projectId}/contract/export`,
          { format: "text" },
          { responseType: "blob" }
        );
        
        const blob = new Blob([res.data], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `contract_${projectId}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Contract downloaded as text");
      } else {
        // Download as native PDF
        const res = await api.post(
          `/contracts/projects/${projectId}/contract/export`,
          { format: "pdf" },
          { responseType: "blob" }
        );
        
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ClientKit_Contract_${projectId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Contract downloaded as PDF");
      }
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyText = () => {
    if (!mdPreview) {
      toast.error("Generate a contract first");
      return;
    }
    
    navigator.clipboard.writeText(mdPreview);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Contract text copied to clipboard");
  };

  const createShareLink = async () => {
    if (!contract) {
      toast.error("Generate a contract first");
      return;
    }
    
    setShareLoading(true);
    try {
      const res = await api.post(`/contracts/projects/${projectId}/contract/share`, {
        expires_days: 30
      });
      
      const fullUrl = `${window.location.origin}${res.data.url}`;
      setShareUrl(fullUrl);
      navigator.clipboard.writeText(fullUrl);
      toast.success("Share link created and copied to clipboard!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create share link");
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  const sendForSignature = async () => {
    if (!contract) {
      toast.error("Generate a contract first");
      return;
    }
    
    setSendingForSignature(true);
    try {
      const res = await api.post(`/contracts/projects/${projectId}/contract/send-for-signature`, {
        client_name: client?.name || client?.company || "",
        client_email: client?.email || "",
        expires_days: 14
      });
      
      setSignatureStatus({
        status: "pending",
        token: res.data.token,
        url: res.data.url,
        expires_at: res.data.expires_at
      });
      
      const signUrl = `${window.location.origin}${res.data.url}`;
      navigator.clipboard.writeText(signUrl);
      toast.success("Signing link created and copied! Send it to your client.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create signing request");
    } finally {
      setSendingForSignature(false);
    }
  };

  const copySigningLink = () => {
    if (signatureStatus?.url) {
      const signUrl = `${window.location.origin}${signatureStatus.url}`;
      navigator.clipboard.writeText(signUrl);
      toast.success("Signing link copied!");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <EntitlementGate feature="contract_builder" user={user}>
      <div className="space-y-6" data-testid="contract-builder">
        <Toaster position="top-center" richColors />
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={`/projects/${projectId}`}
              className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
                <FileText className="w-6 h-6 text-indigo-400" />
                Contract Builder
              </h1>
              <p className="text-indigo-400 text-sm mt-1">
                {project?.title} • {client?.name || client?.company || "No client"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-400/50 hover:bg-slate-600/50 text-slate-400 rounded-lg transition-colors text-sm"
              data-testid="load-template-btn"
            >
              <BookOpen className="w-4 h-4" />
              {selectedTemplate ? "Change Template" : "Load Template"}
            </button>
            {autoSaved && (
              <span className="text-xs bg-slate-400/50 text-indigo-400 px-3 py-1 rounded-full flex items-center gap-1.5 animate-fade-in">
                <Save className="w-3 h-3" />
                Draft saved
              </span>
            )}
            {contract && (
              <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full">
                Version {contract.version}
              </span>
            )}
          </div>
        </div>

        {/* Template Picker */}
        {showTemplates && (
          <Card className="bg-slate-800/50 border-indigo-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-indigo-500 text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-indigo-400" />
                  Choose a Template
                </CardTitle>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="text-indigo-400 hover:text-indigo-500"
                >
                  ×
                </button>
              </div>
              <CardDescription className="text-indigo-400">
                Start from a pre-built template or create from scratch
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TemplateQuickPicker onSelect={handleTemplateSelect} />
            </CardContent>
          </Card>
        )}

        {/* Selected Template Badge */}
        {selectedTemplate && !showTemplates && (
          <div className="flex items-center gap-2 px-3 py-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
            <BookOpen className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-indigo-400">Using template: {selectedTemplate.name}</span>
            <button
              onClick={() => {
                setSelectedTemplate(null);
                setMdPreview("");
              }}
              className="ml-auto text-indigo-400 hover:text-indigo-400 text-xs"
            >
              Clear
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Form Inputs */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-lg">Contract Details</CardTitle>
              <CardDescription className="text-indigo-400">
                Customize your one-page agreement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Service Type */}
              <div className="space-y-1.5">
                <label className="text-sm text-indigo-400">Service Type</label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {SERVICE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-slate-400">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Timeline */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm text-indigo-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm text-indigo-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Payment Terms */}
              <div className="space-y-1.5">
                <label className="text-sm text-indigo-400 flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Payment Terms
                </label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder="e.g., Net 7, 50% upfront"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Revision Policy */}
              <div className="space-y-1.5">
                <label className="text-sm text-indigo-400 flex items-center gap-1">
                  <RefreshCw className="w-3.5 h-3.5" />
                  Revision Policy
                </label>
                <input
                  type="text"
                  value={revisionPolicy}
                  onChange={(e) => setRevisionPolicy(e.target.value)}
                  placeholder="e.g., 1 round included"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Freelancer Name */}
              <div className="space-y-1.5">
                <label className="text-sm text-indigo-400">Your Name (as Freelancer)</label>
                <input
                  type="text"
                  value={freelancerName}
                  onChange={(e) => setFreelancerName(e.target.value)}
                  placeholder={user?.name || "Your name"}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Additional Notes */}
              <div className="space-y-1.5">
                <label className="text-sm text-indigo-400">Additional Notes</label>
                <Textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Any special terms or conditions..."
                  className="min-h-[80px] bg-slate-900/50 border-slate-600 text-indigo-500 placeholder:text-slate-400"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  onClick={generateContract}
                  disabled={generating}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-indigo-500 to-orange-600 hover:from-indigo-600 hover:to-orange-700 text-indigo-500 font-medium rounded-xl transition-all disabled:opacity-50"
                  data-testid="generate-contract-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Contract
                    </>
                  )}
                </button>
              </div>
              
              {mdPreview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => exportContract("pdf")}
                    disabled={exporting}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-400/50 hover:bg-slate-600/50 text-indigo-500 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="export-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                  <button
                    onClick={() => exportContract("text")}
                    disabled={exporting}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-400/50 hover:bg-slate-600/50 text-indigo-500 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="export-text-btn"
                  >
                    <FileOutput className="w-4 h-4" />
                    Download .txt
                  </button>
                  <button
                    onClick={copyText}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-400/50 hover:bg-slate-600/50 text-indigo-500 rounded-lg transition-colors"
                    data-testid="copy-text-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-green-400" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Share Link Section */}
              {mdPreview && (
                <div className="border-t border-slate-700/50 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-indigo-400 flex items-center gap-1.5">
                      <Share2 className="w-4 h-4" />
                      Client Preview Link
                    </label>
                    {!shareUrl && (
                      <button
                        onClick={createShareLink}
                        disabled={shareLoading}
                        className="text-xs text-indigo-400 hover:text-indigo-400 flex items-center gap-1"
                      >
                        {shareLoading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Link2 className="w-3 h-3" />
                        )}
                        {shareLoading ? "Creating..." : "Create Link"}
                      </button>
                    )}
                  </div>
                  {shareUrl ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={shareUrl}
                        readOnly
                        className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-slate-400 text-sm"
                      />
                      <button
                        onClick={copyShareLink}
                        className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded-lg transition-colors"
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded-lg transition-colors"
                        title="Preview"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">
                      Create a link to share this contract with your client for review.
                    </p>
                  )}
                </div>
              )}
              
              {/* E-Signature Section */}
              {mdPreview && (
                <div className="border-t border-slate-700/50 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-indigo-400 flex items-center gap-1.5">
                      <PenTool className="w-4 h-4" />
                      E-Signature
                    </label>
                    {signatureStatus?.status === "signed" && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Signed
                      </span>
                    )}
                  </div>
                  
                  {signatureStatus?.status === "signed" ? (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                      <p className="text-sm text-green-400 mb-2">
                        Contract signed on {new Date(signatureStatus.signed_at).toLocaleDateString()}
                      </p>
                      {signatureStatus.signature_data && (
                        <img 
                          src={signatureStatus.signature_data} 
                          alt="Signature" 
                          className="h-16 bg-white rounded p-2"
                        />
                      )}
                    </div>
                  ) : signatureStatus?.status === "pending" ? (
                    <div className="space-y-2">
                      <p className="text-xs text-indigo-400 bg-indigo-500/10 px-3 py-2 rounded-lg">
                        Awaiting client signature (expires {new Date(signatureStatus.expires_at).toLocaleDateString()})
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={`${window.location.origin}${signatureStatus.url}`}
                          readOnly
                          className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-slate-400 text-sm"
                        />
                        <button
                          onClick={copySigningLink}
                          className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded-lg transition-colors"
                          title="Copy signing link"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <a
                          href={signatureStatus.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded-lg transition-colors"
                          title="Preview signing page"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400">
                        Send this contract to your client for electronic signature.
                      </p>
                      <button
                        onClick={sendForSignature}
                        disabled={sendingForSignature}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors disabled:opacity-50"
                        data-testid="send-for-signature-btn"
                      >
                        {sendingForSignature ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <PenTool className="w-4 h-4" />
                            Send for E-Signature
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Preview */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-lg">Preview</CardTitle>
              <CardDescription className="text-indigo-400">
                {mdPreview ? "Your generated contract (Markdown)" : "Fill in the form and click Generate"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mdPreview ? (
                <div className="bg-slate-900/50 rounded-xl p-4 max-h-[600px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-slate-400 font-mono leading-relaxed">
                    {mdPreview}
                  </pre>
                </div>
              ) : (
                <div className="bg-slate-900/30 rounded-xl p-8 text-center border border-dashed border-slate-700">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">
                    Your contract preview will appear here
                  </p>
                  <p className="text-xs text-slate-600 mt-2">
                    Click &quot;Generate Contract&quot; to create from project data
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </EntitlementGate>
  );
}
